// @ts-strict-ignore
import { SERVICE_FAILURE_DEFAULT_VALUES } from './constants';

import type { Action, ServiceObjectInAction } from '~/dashboard/views/workflows/store/engine/types';
import type { FormState } from './ServiceLocalSettingsTab/types';
import type { NodeEditorFormAdapter } from '../useNodeEditorForm';

import { generatePublicId } from '~/dashboard/views/workflows/store/engine/utils';
import type {
  FormattedServiceConfigOption,
  ServiceConfig,
} from '~/dashboard/views/settings/service-configuration/types';
import {
  CredentialOwner,
  DefaultLevel,
} from '~/dashboard/views/settings/service-configuration/types';
import {
  formatValue,
  getCredentialOwnerOption,
} from '~/dashboard/views/settings/service-configuration/helpers/formatData';
import {
  AlloyOption,
  UnknowOption,
} from '~/dashboard/views/settings/service-configuration/helpers/fixture';
import {
  buildServiceFailureDefaultOption,
  convertServiceOptionToActionOption,
} from './serviceOptionHelpers';

const addDisplayNameOption = (
  displayNameOption: FormattedServiceConfigOption,
  serviceName: Action['service']['name'],
  formattedServiceConfigOptions: Array<FormattedServiceConfigOption>,
): Array<FormattedServiceConfigOption> => {
  const isDisplayNameSameAsServiceName = displayNameOption.value === serviceName;
  if (!isDisplayNameSameAsServiceName) {
    return [displayNameOption, ...formattedServiceConfigOptions];
  }
  return formattedServiceConfigOptions;
};

export const convertServiceConfigOption = (
  serviceConfig: ServiceConfig,
  options: Action['options'],
  focusActionCredentialOwner: ServiceObjectInAction['credentialOwner'],
) => {
  if (!serviceConfig) {
    return {
      credentialOwner: CredentialOwner.unknown,
      formattedServiceConfigOptions: [],
    };
  }
  const serviceConfigOptionsMapping = serviceConfig.serviceConfigOptions?.reduce((acc, option) => {
    acc[option.serviceConfigOptionId] = option;
    return acc;
  }, {});

  const customerCredentialOwner = serviceConfig.selectedCustomerOptions?.credentialOwner;
  let credentialOwnerOption = UnknowOption;
  const optionMap = new Map();
  for (const [key, option] of Object.entries(options)) {
    const optionMetaData = serviceConfigOptionsMapping[option.serviceConfigOptionId];
    if (optionMetaData) {
      const value = formatValue(option, optionMetaData);
      if (option.serviceConfigOptionId) {
        optionMap.set(option.serviceConfigOptionId, {
          serviceConfigOptionId: option.serviceConfigOptionId,
          value,
          isUsingDefault: false,
          defaultLevel: DefaultLevel.NULL,
          key,
          publicId: option.publicId,
        });

        // If there is a workflow-level owner field, we set the credential owner by using the
        // credential owner in the focusAction.
        if (optionMetaData.isOwner) {
          credentialOwnerOption = getCredentialOwnerOption(focusActionCredentialOwner);
        }
      }
    }
  }

  serviceConfig.selectedCustomerOptions?.serviceConfigOptions?.forEach(option => {
    const optionMetaData = serviceConfigOptionsMapping[option.serviceConfigOptionId];
    if (optionMetaData) {
      const value = formatValue(option, optionMetaData);
      if (optionMap.has(option.serviceConfigOptionId)) {
        optionMap.get(option.serviceConfigOptionId).defaultValue = value;
        optionMap.get(option.serviceConfigOptionId).defaultLevel = DefaultLevel.CUSTOMER;
        const mapOption = optionMap.get(option.serviceConfigOptionId);
        mapOption.defaultCredentialOwner = customerCredentialOwner;
      } else {
        optionMap.set(option.serviceConfigOptionId, {
          serviceConfigOptionId: option.serviceConfigOptionId,
          value,
          defaultValue: value,
          isUsingDefault: true,
          defaultLevel: DefaultLevel.CUSTOMER,
          key: optionMetaData.slug,
          publicId: generatePublicId('actionoption'),
          defaultCredentialOwner: customerCredentialOwner,
        });

        // If the owner field is NOT found at the workflow level but found at the customer level,
        // we overwrite the credential owner to the customer-level credential owner.
        if (optionMetaData.isOwner) {
          credentialOwnerOption = getCredentialOwnerOption(customerCredentialOwner);
        }
      }
    }
  });

  serviceConfig.selectedAlloyOptions?.serviceConfigOptions?.forEach(option => {
    const optionMetaData = serviceConfigOptionsMapping[option.serviceConfigOptionId];
    if (optionMetaData) {
      const value = formatValue(option, optionMetaData);

      // Check if there is a customer-level value set.
      if (optionMap.has(option.serviceConfigOptionId)) {
        // If there has customer-level value, we set the Alloy level value as defaultValue.
        if (
          !optionMap.get(option.serviceConfigOptionId).isUsingDefault
          && optionMap.get(option.serviceConfigOptionId).defaultLevel !== DefaultLevel.CUSTOMER
        ) {
          optionMap.get(option.serviceConfigOptionId).defaultValue = value;
          optionMap.get(option.serviceConfigOptionId).defaultLevel = DefaultLevel.ALLOY;
        }
      } else {
        // If there hasn't customer-level value, we use the Alloy value.
        optionMap.set(option.serviceConfigOptionId, {
          serviceConfigOptionId: option.serviceConfigOptionId,
          value,
          defaultValue: value,
          isUsingDefault: true,
          defaultLevel: DefaultLevel.ALLOY,
          key: optionMetaData.slug,
          publicId: generatePublicId('actionoption'),
        });
        // If the owner field is NOT found at the customer level but found at the alloy level,
        // we overwrite the credential owner to the alloy-level credential owner.
        if (optionMetaData.isOwner) {
          credentialOwnerOption = AlloyOption;
        }
      }
    }
  });

  serviceConfig.serviceConfigOptions?.forEach(optionMetaData => {
    const value = formatValue(null, optionMetaData);
    // If the field is required and we don't have value in any level, we place a empty filed.
    if (!optionMap.has(optionMetaData.serviceConfigOptionId) && optionMetaData.isRequired) {
      optionMap.set(optionMetaData.serviceConfigOptionId, {
        serviceConfigOptionId: optionMetaData.serviceConfigOptionId,
        value,
        isUsingDefault: false,
        defaultLevel: DefaultLevel.NULL,
        key: optionMetaData.slug,
        publicId: generatePublicId('actionoption'),
      });
    }
  });

  return {
    credentialOwnerOption,
    formattedServiceConfigOptions: Array.from(optionMap.values()),
  };
};

const convertFormatedServiceConfigToActionOption = formattedServiceConfigOptions => {
  const newOptions = formattedServiceConfigOptions.reduce((acc, option) => {
    if (!option.isUsingDefault) {
      let value;
      let selectedValues;
      if (Array.isArray(option.value)) {
        value = option.value.map(v => v.label).join();
        selectedValues = option.value.map(v => parseInt(v.value, 10));
      } else if (option.value instanceof Object && option.value.constructor === Object) {
        value = option.value.value;
        selectedValues = null;
      } else {
        value = option.value;
        selectedValues = null;
      }
      acc[option.key] = {
        value,
        jsonValue: null,
        publicId: option.publicId,
        serviceConfigOptionId: option.serviceConfigOptionId,
        selectedValues,
      };
    }
    return acc;
  }, {});
  return newOptions;
};

// form:
export const serviceLocalSettingFormAdapter: NodeEditorFormAdapter<FormState> = {
  getDefaultFormValues(action, workflowProps) {
    const { serviceFailureDefault, service: actionService } = action;
    const servcie = workflowProps.services[actionService.internalName];

    return {
      recurringWorkflow: null, // handled within ServiceOptionsTab: requires ajax
      serviceFailureDefaultOption: buildServiceFailureDefaultOption(serviceFailureDefault),
      displayNameOption: null, // handled within ServiceOptionsTab: requires ajax
      credentialOwnerOption: UnknowOption, // handled within ServiceOptionsTab: requires ajax
      formattedServiceConfigOptions: [], // handled within ServiceOptionsTab: requires ajax
      serviceId: servcie.service_id,
      serviceName: servcie.name,
    };
  },
  mapFormDataToAction(data, sourceAction) {
    const recurringWorkflowData = data.recurringWorkflow
      ? convertServiceOptionToActionOption([data.recurringWorkflow])
      : {};
    const serviceConfigOptions = addDisplayNameOption(
      data.displayNameOption,
      sourceAction.service.name,
      data.formattedServiceConfigOptions,
    );

    // we want to persist the options that don't have a serviceConfigOptionId
    const optionsWithoutServConfigOption = {};
    for (const [key, option] of Object.entries(sourceAction.options)) {
      if (!option.serviceConfigOptionId) {
        optionsWithoutServConfigOption[key] = option;
      }
    }
    return {
      ...sourceAction,
      options: {
        ...optionsWithoutServConfigOption,
        ...convertFormatedServiceConfigToActionOption(serviceConfigOptions),
        ...recurringWorkflowData,
      },
      serviceFailureDefault: SERVICE_FAILURE_DEFAULT_VALUES[data.serviceFailureDefaultOption.label],
      service: {
        ...sourceAction.service,
        credentialOwner: data.credentialOwnerOption.value,
      },
    };
  },
};
